import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icone } from '../../components/Icone';
import { Valor } from '../../components/Valor';
import { api } from '../../lib/api';

/**
 * O sino, no canto superior direito.
 *
 * Saiu do menu lateral por um motivo que so aparece depois de algumas
 * semanas de uso: "Notificacoes" era um DESTINO entre outros doze, e um
 * destino compete com os outros. Um aviso nao compete — ele interrompe de
 * leve, de onde a pessoa estiver, e volta para o lugar de onde veio.
 *
 * O painel aqui e uma previa, e nao a tela inteira: mostra as ultimas e leva
 * para Notificacoes quando ha o que corrigir. Editar categoria dentro de um
 * menu suspenso de trezentos pixels seria pior do que nao editar.
 *
 * Zero nunca aparece no numero. Distintivo sempre visivel ensina a ignorar o
 * distintivo — e ai ele falha justamente no dia em que ha algo.
 */
export function Sino() {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);

  const dados = useQuery({ queryKey: ['a-conferir'], queryFn: () => api.aConferir() });
  const total = dados.data?.total ?? 0;
  const pendentes = dados.data?.transactions ?? [];
  const conferidasHoje = dados.data?.conferidasHoje ?? [];

  /** Clique fora e Esc fecham. Um painel que so fecha no proprio botao prende. */
  useEffect(() => {
    if (!aberto) return;

    const foraDaqui = (evento: MouseEvent) => {
      if (!caixa.current?.contains(evento.target as Node)) setAberto(false);
    };
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAberto(false);
    };

    document.addEventListener('mousedown', foraDaqui);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', foraDaqui);
      document.removeEventListener('keydown', tecla);
    };
  }, [aberto]);

  const novidadesDoDia = pendentes.length > 0 ? pendentes : conferidasHoje;

  return (
    <div className="sino" ref={caixa}>
      <button
        type="button"
        className={`sino-botao${total > 0 ? ' tem' : ''}`}
        aria-expanded={aberto}
        aria-haspopup="dialog"
        aria-label={total > 0 ? `Notificacoes: ${total} a conferir` : 'Notificacoes'}
        title={total > 0 ? `${total} lancamento(s) a conferir` : 'Nada a conferir'}
        onClick={() => setAberto(!aberto)}
      >
        <Icone nome="notificacoes" />
        {total > 0 && <span className="distintivo sino-numero">{total > 99 ? '99+' : total}</span>}
      </button>

      {aberto && (
        <div className="sino-painel" role="dialog" aria-label="Novidades">
          <div className="sino-topo">
            <strong>{total > 0 ? 'A conferir' : 'Novidades de hoje'}</strong>
            <Link to="/notificacoes" onClick={() => setAberto(false)}>
              Ver todas
            </Link>
          </div>

          {novidadesDoDia.length === 0 ? (
            <p className="sino-vazio">
              Nada novo. Quando o banco entregar uma movimentacao, ela aparece aqui.
            </p>
          ) : (
            <ul className="sino-lista">
              {novidadesDoDia.slice(0, 6).map((transaction) => (
                <li key={transaction.id}>
                  <Link to="/notificacoes" onClick={() => setAberto(false)}>
                    <span className="sino-linha">
                      <span className="sino-nome">{transaction.displayName}</span>
                      <Valor cents={transaction.amountCents} />
                    </span>
                    <span className="sino-meta">
                      {transaction.categoryName ?? 'sem categoria'}
                      {transaction.counterpartyName ? ` · ${transaction.counterpartyName}` : ''}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {total > novidadesDoDia.slice(0, 6).length && (
            <div className="sino-rodape">
              <Link to="/notificacoes" onClick={() => setAberto(false)}>
                Ver os outros {total - Math.min(6, novidadesDoDia.length)}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
